import { FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    OutlinedInput,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Firmware } from 'types/firmware';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { dispatch } from 'store';
import FileUploader from 'ui-component/dropzone/fileUploader';
import { startSnackbar } from 'store/slices/snackbar';
import Utilities from 'utils/utilities';
import ReactQuill from 'react-quill';

interface ChildComponentProps {
    firmware?: Firmware;

    onSave(firmware: Firmware): any;

    onClose(): any;
}

const FirmwareEdit: FC<ChildComponentProps> = (props) => {
    const { firmware, onSave, onClose } = props;
    const [open, setOpen] = useState(false);
    const [currentFirmware, setCurrentFirmware] = useState(firmware);
    const { t } = useTranslation();
    const theme = useTheme();
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const [currentVersion, setCurrentVersion] = useState<string>('');
    const versionRegex = /^\d+\.\d+$/;
    const utilities = new Utilities();

    useEffect(() => {
        if (firmware === undefined) {
            setOpen(false);
        } else {
            setOpen(true);
            setCurrentVersion(utilities.getVersion(firmware.major!, firmware.minor!));
            setCurrentFirmware(firmware);
        }
    }, [firmware]);

    const handleZipRead = (zipContent: ArrayBuffer) => {
        setCurrentFirmware((prev) => ({
            ...prev!,
            zipFile: zipContent
        }));
    };

    return (
        <Dialog sx={{ maxWidth: window.innerWidth, width: window.innerWidth - 10 }} maxWidth="lg" open={open} onClose={onClose}>
            <DialogTitle id="alert-dialog-title">
                {t('EDIT')} firmware {firmware?.major}.{firmware?.minor}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: '6px!important', width: mobileDevice ? window.innerWidth - 65 : '100%' }}>
                <Grid container>
                    <Grid item md={3} xs={6} sx={{ mt: 2 }}>
                        <FormControl fullWidth required error={!versionRegex.test(currentVersion)}>
                            <InputLabel>{t('VERSION')}</InputLabel>
                            <OutlinedInput
                                defaultValue={utilities.getVersion(firmware?.major!, firmware?.minor!)}
                                onChange={(d) => {
                                    const input = d.target.value;
                                    setCurrentVersion(input);
                                    if (versionRegex.test(input)) {
                                        const [major, minor] = input.split('.').map(Number);
                                        setCurrentFirmware({
                                            ...currentFirmware!,
                                            major,
                                            minor
                                        });
                                    }
                                }}
                                type="text"
                                label={t('NAME')}
                            />
                            <FormHelperText>{!versionRegex.test(currentVersion) ? t('INSERT_A_VALID_VERSION') : ' '}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item md={4} xs={12} sx={{ mb: 2, mt: 2, textAlign: !mobileDevice ? 'center' : 'left' }}>
                        <DateTimePicker
                            label={t('RELEASE_DATE')}
                            value={currentFirmware?.releaseDate}
                            onChange={(newDate) => {
                                if (!newDate) return;
                                setCurrentFirmware((prev) => ({
                                    ...prev!,
                                    releaseDate: new Date(newDate as any)
                                }));
                            }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item md={12} xs={12} sx={{ mb: 2, mt: 2 }}>
                        <Box sx={{ height: '200px', paddingBottom: '40px' }}>
                            <ReactQuill
                                className={theme.palette.mode === 'dark' ? 'dark-my-quill-editor' : 'my-quill-editor'}
                                value={currentFirmware?.releaseNote}
                                onChange={(d) => {
                                    const e = {
                                        ...currentFirmware!,
                                        releaseNote: d
                                    };
                                    setCurrentFirmware(e);
                                }}
                                theme="snow"
                                placeholder={t('RELEASE_NOTES')}
                                style={{ height: '100%' }}
                            />
                        </Box>
                    </Grid>
                    <Grid container>
                        <Grid item md={6} xs={12} sx={{ mb: 2, mt: 2 }}>
                            <Typography variant="body1" marginBottom="5px">
                                {t('UPLOAD_ZIP_FILE')}*
                            </Typography>
                            <FileUploader onZipRead={handleZipRead} />
                        </Grid>
                    </Grid>
                </Grid>
                <Typography marginTop="5px"> * {t('MANDATORY_FIELDS')}</Typography>
            </DialogContent>
            <DialogActions sx={{ marginRight: '16px' }}>
                <Button
                    onClick={() => {
                        setCurrentFirmware(undefined);
                        onClose();
                    }}
                    autoFocus
                    style={{
                        border: '2px solid',
                        borderColor: theme.palette.secondary.main,
                        borderRadius: 7
                    }}
                >
                    <Typography sx={{ color: theme.palette.primary.main }}> {t('CANCEL')}</Typography>
                </Button>
                <Button
                    style={{
                        border: '2px solid',
                        borderColor: theme.palette.primary.main,
                        borderRadius: 7,
                        background: theme.palette.primary.main
                    }}
                    onClick={() => {
                        if (versionRegex.test(currentVersion)) {
                            setCurrentFirmware(undefined);
                            onSave(currentFirmware!);
                        } else {
                            dispatch(startSnackbar(t('INSERT_A_VALID_VERSION'), 'error'));
                        }
                    }}
                >
                    <Typography style={{ color: '#fff' }}>{t('SAVE')}</Typography>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FirmwareEdit;
